import { z } from "zod";
import { PLUGIN_CATALOG, type PluginId } from "./plugins";

/**
 * Importar do VS Code (e editores derivados: Cursor, VSCodium, Windsurf). As extensões instaladas
 * lá viram os plugins equivalentes do Kompil, gravados na CONTA da pessoa — valem em qualquer
 * computador em que ela entrar, sem instalar plugin por plugin.
 */

/** Extensão do VS Code (minúsculas, "publisher.nome") → plugin do Kompil. */
export const VSCODE_EQUIVALENTS: Readonly<Record<string, PluginId>> = {
  // IA
  "github.copilot": "kompil.github-copilot",
  "github.copilot-chat": "kompil.github-copilot",
  "anthropic.claude-code": "kompil.claude-agent",
  "saoudrizwan.claude-dev": "kompil.claude-agent",
  // Linguagens e ferramentas
  "ritwickdey.liveserver": "kompil.live-server",
  "ms-vscode.live-server": "kompil.live-server",
  "ms-azuretools.vscode-docker": "kompil.docker",
  "ms-azuretools.vscode-containers": "kompil.docker",
  "docker.docker": "kompil.docker",
  "redhat.java": "kompil.java",
  "vscjava.vscode-java-pack": "kompil.java",
  "vscjava.vscode-java-debug": "kompil.java",
  "vscjava.vscode-maven": "kompil.java",
  "vscjava.vscode-gradle": "kompil.java",
  "vscjava.vscode-java-test": "kompil.java",
  "vscjava.vscode-java-dependency": "kompil.java",
  "vmware.vscode-spring-boot": "kompil.java",
  "vscjava.vscode-spring-boot-dashboard": "kompil.java",
  "ms-python.python": "kompil.python",
  "ms-python.vscode-pylance": "kompil.python",
  "ms-python.debugpy": "kompil.python",
  "ms-python.black-formatter": "kompil.python",
  "ms-python.pylint": "kompil.python",
  "ms-python.flake8": "kompil.python",
  "charliermarsh.ruff": "kompil.python",
  "ms-python.vscode-python-envs": "kompil.python",
  "primafuture.open-php-html-js-in-browser": "kompil.live-server",
  "esbenp.prettier-vscode": "kompil.prettier",
  "gitguardian.gitguardian": "kompil.secret-scanner",
  // Editor
  "vscodevim.vim": "kompil.vim",
  "asvetliakov.vscode-neovim": "kompil.vim",
  "oderwat.indent-rainbow": "kompil.indent-guides",
  "coenraads.bracket-pair-colorizer": "kompil.rainbow-brackets",
  "coenraads.bracket-pair-colorizer-2": "kompil.rainbow-brackets",
  "2gua.rainbow-brackets": "kompil.rainbow-brackets",
  "wayou.vscode-todo-highlight": "kompil.todo-highlighter",
  "gruntfuggly.todo-tree": "kompil.todo-highlighter",
  "aaron-bond.better-comments": "kompil.todo-highlighter",
  "naumovs.color-highlight": "kompil.color-preview",
  "kamikillerto.vscode-colorize": "kompil.color-preview",
  "shardulm94.trailing-spaces": "kompil.whitespace",
  "xabikos.javascriptsnippets": "kompil.snippets",
  "dsznajder.es7-react-js-snippets": "kompil.snippets",
  "burkeholland.simple-react-snippets": "kompil.snippets",
  "sdras.vue-vscode-snippets": "kompil.snippets",
  "shd101wyy.markdown-preview-enhanced": "kompil.markdown-preview",
  "yzhang.markdown-all-in-one": "kompil.markdown-preview",
  "bierner.markdown-preview-github-styles": "kompil.markdown-preview",
  "ms-vscode.wordcount": "kompil.doc-stats",
  // Colaboração e Git
  "eamodio.gitlens": "kompil.authorship",
  "waderyan.gitblame": "kompil.authorship",
  "ms-vsliveshare.vsliveshare": "kompil.cursor-names",
  // Aparência
  "pkief.material-icon-theme": "kompil.file-icons",
  "vscode-icons-team.vscode-icons": "kompil.file-icons",
  "miguelsolorio.symbols": "kompil.file-icons",
  "dracula-theme.theme-dracula": "kompil.theme-dracula",
  "zhuangtongfa.material-theme": "kompil.theme-one-dark",
  "akamud.vscode-theme-onedark": "kompil.theme-one-dark",
  "github.github-vscode-theme": "kompil.theme-github",
  "arcticicestudio.nord-visual-studio-code": "kompil.theme-nord",
  "monokai.theme-monokai-pro-vscode": "kompil.theme-monokai",
  "silofy.hackthebox": "kompil.theme-hacker",
};

/**
 * Plugins do Kompil que já existem como extensões do VS Code na Open VSX (a loja do motor VS Code):
 * na tela de Extensões, aparece a extensão do VS Code (com ícone, avaliações e README dela), e
 * instalar ou habilitar a extensão liga junto o recurso equivalente no editor do Kompil. Os plugins
 * fora desta lista não existem no VS Code e continuam como plugins do Kompil.
 */
export const KOMPIL_FROM_VSCODE: Readonly<Partial<Record<PluginId, string>>> = {
  "kompil.claude-agent": "anthropic.claude-code",
  "kompil.live-server": "ritwickdey.liveserver",
  "kompil.docker": "ms-azuretools.vscode-docker",
  "kompil.java": "redhat.java",
  "kompil.python": "ms-python.python",
  "kompil.authorship": "eamodio.gitlens",
  "kompil.file-icons": "pkief.material-icon-theme",
  "kompil.snippets": "xabikos.javascriptsnippets",
  "kompil.markdown-preview": "shd101wyy.markdown-preview-enhanced",
  "kompil.theme-dracula": "dracula-theme.theme-dracula",
  "kompil.theme-one-dark": "zhuangtongfa.material-theme",
  "kompil.theme-github": "github.github-vscode-theme",
  "kompil.theme-nord": "arcticicestudio.nord-visual-studio-code",
  "kompil.prettier": "esbenp.prettier-vscode",
  "kompil.vim": "vscodevim.vim",
  "kompil.indent-guides": "oderwat.indent-rainbow",
  "kompil.todo-highlighter": "wayou.vscode-todo-highlight",
  "kompil.color-preview": "naumovs.color-highlight",
  "kompil.whitespace": "shardulm94.trailing-spaces",
};

/** O plugin vem do VS Code (não aparece sozinho na tela de Extensões)? */
export const isVsCodeBacked = (id: string) => id in KOMPIL_FROM_VSCODE;

/** Recurso do Kompil que uma extensão do VS Code liga no editor do Kompil (se houver). */
export const kompilPluginFor = (extensionId: string): PluginId | null =>
  VSCODE_EQUIVALENTS[extensionId.toLowerCase()] ?? null;

/**
 * Extensões cujo recurso o Kompil já traz sem plugin (destaque de sintaxe, diagnósticos, Git,
 * colaboração ao vivo…): aparecem como "já vem no Kompil", não como "sem equivalente".
 */
export const VSCODE_BUILTIN: ReadonlySet<string> = new Set([
  "golang.go",
  "rust-lang.rust-analyzer",
  "ms-vscode.cpptools",
  "ms-vscode.cpptools-extension-pack",
  "ms-dotnettools.csharp",
  "ms-dotnettools.csdevkit",
  "redhat.vscode-yaml",
  "redhat.vscode-xml",
  "tamasfe.even-better-toml",
  "bmewburn.vscode-intelephense-client",
  "rebornix.ruby",
  "shopify.ruby-lsp",
  "swiftlang.swift-vscode",
  "fwcd.kotlin",
  "mathiasfrohlich.kotlin",
  "dbaeumer.vscode-eslint",
  "formulahendry.auto-rename-tag",
  "formulahendry.auto-close-tag",
  "christian-kohler.path-intellisense",
  "mikestead.dotenv",
  "mtxr.sqltools",
  "mhutchie.git-graph",
  "donjayamanne.githistory",
  "github.vscode-pull-request-github",
  "ms-vscode.powershell",
  "timonwong.shellcheck",
  "foxundermoon.shell-format",
  "davidanson.vscode-markdownlint",
  "ms-vscode-remote.remote-containers",
  "ms-vscode-remote.remote-wsl",
  "ms-vscode-remote.remote-ssh",
  "ms-dotnettools.vscode-dotnet-runtime",
  // A interface do Kompil já é em português.
  "ms-ceintl.vscode-language-pack-pt-br",
  // Autocompletar no editor, lista de projetos, executar e "abrir no GitHub" (painel Git).
  "visualstudioexptteam.vscodeintellicode",
  "visualstudioexptteam.intellicode-api-usage-examples",
  "alefragnani.project-manager",
  "miramac.vscode-exec-node",
  "formulahendry.code-runner",
  "ziyasal.vscode-open-in-github",
  "donjayamanne.git-extension-pack",
]);

/** Tema do VS Code ("workbench.colorTheme") → tema do Kompil. */
const THEME_RULES: Array<[RegExp, PluginId]> = [
  [/dracula/i, "kompil.theme-dracula"],
  [/one dark/i, "kompil.theme-one-dark"],
  [/^github/i, "kompil.theme-github"],
  [/nord/i, "kompil.theme-nord"],
  [/monokai/i, "kompil.theme-monokai"],
  [/hack ?the ?box|hacker|matrix|terminal green/i, "kompil.theme-hacker"],
  [/light|solarized light|quiet light|high contrast light/i, "kompil.theme-light"],
];

export function themeFor(colorTheme: string | null | undefined): PluginId | null {
  if (!colorTheme) return null;
  for (const [re, id] of THEME_RULES) if (re.test(colorTheme)) return id;
  return null;
}

const isThemePlugin = (id: PluginId) =>
  PLUGIN_CATALOG.find((p) => p.id === id)?.permissions.some(
    (perm) => perm === "editor.theme" || perm === "ui.theme",
  ) ?? false;

const EXTENSION_ID = /^[a-z0-9][a-z0-9-]*\.[a-z0-9][a-z0-9._-]*$/;

export const VSCODE_SOURCES = [
  "vscode",
  "vscode-insiders",
  "cursor",
  "vscodium",
  "windsurf",
  "profile",
  "list",
] as const;
export type VsCodeSource = (typeof VSCODE_SOURCES)[number];

export const VSCODE_SOURCE_LABELS: Record<VsCodeSource, string> = {
  vscode: "VS Code",
  "vscode-insiders": "VS Code Insiders",
  cursor: "Cursor",
  vscodium: "VSCodium",
  windsurf: "Windsurf",
  profile: "Perfil exportado do VS Code",
  list: "Lista de extensões",
};

export const VsCodeImportInput = z.object({
  source: z.enum(VSCODE_SOURCES),
  extensions: z
    .array(z.string().trim().toLowerCase().regex(EXTENSION_ID, "Extensão inválida (use publisher.nome)"))
    .max(2_000),
  /** "workbench.colorTheme" do settings.json, quando conhecido. */
  theme: z.string().trim().max(200).optional(),
});
export type VsCodeImportInput = z.infer<typeof VsCodeImportInput>;

export interface VsCodeMatch {
  /** Plugins do Kompil a instalar (sem repetição; no máximo um tema). */
  plugins: PluginId[];
  /** extensão → plugin que a substitui. */
  matched: Array<{ extension: string; plugin: PluginId }>;
  /** Já vêm no Kompil, sem plugin. */
  builtin: string[];
  /** Ainda sem equivalente no Kompil. */
  unmatched: string[];
  /** Tema escolhido (do settings.json; senão, da primeira extensão de tema). */
  theme: PluginId | null;
}

/** Normaliza ids ("Publisher.Nome@1.2.3" → "publisher.nome"), sem repetição e só os válidos. */
export function normalizeExtensions(ids: Iterable<string>): string[] {
  const out = new Set<string>();
  for (const raw of ids) {
    const id = raw
      .trim()
      .replace(/@[^@\s]*$/, "")
      .toLowerCase();
    if (EXTENSION_ID.test(id)) out.add(id);
  }
  return [...out];
}

export function matchVsCode(extensions: Iterable<string>, colorTheme?: string | null): VsCodeMatch {
  const ids = normalizeExtensions(extensions);
  const matched: VsCodeMatch["matched"] = [];
  const builtin: string[] = [];
  const unmatched: string[] = [];
  for (const id of ids) {
    const plugin = VSCODE_EQUIVALENTS[id];
    if (plugin) matched.push({ extension: id, plugin });
    else if (VSCODE_BUILTIN.has(id)) builtin.push(id);
    else unmatched.push(id);
  }
  // Tema conhecido (settings.json): vale ele, mesmo que seja o escuro padrão (= sem tema no Kompil).
  // Desconhecido: a primeira extensão de tema instalada.
  const theme =
    typeof colorTheme === "string"
      ? themeFor(colorTheme)
      : (matched.map((m) => m.plugin).find(isThemePlugin) ?? null);
  const plugins = [...new Set(matched.map((m) => m.plugin).filter((p) => !isThemePlugin(p)))];
  if (theme) plugins.push(theme);
  return { plugins, matched, builtin, unmatched, theme };
}

/** Saída de `code --list-extensions` (com ou sem `--show-versions`), uma por linha. */
export function parseExtensionList(text: string): string[] {
  return normalizeExtensions(text.split(/[\s,;]+/).filter(Boolean));
}

/**
 * Perfil exportado do VS Code (arquivo .code-profile ou o gist de "Exportar perfil"): os campos
 * `extensions` e `settings` vêm como JSON dentro de texto.
 */
export function parseCodeProfile(text: string): {
  extensions: string[];
  theme: string | null;
  name: string | null;
} {
  const profile = JSON.parse(text) as { name?: unknown; extensions?: unknown; settings?: unknown };
  const decode = (v: unknown) => (typeof v === "string" ? (JSON.parse(v) as unknown) : v);
  const list = decode(profile.extensions);
  const extensions = normalizeExtensions(
    Array.isArray(list)
      ? list
          .filter((e) => !(e as { disabled?: boolean }).disabled)
          .map((e) => (e as { identifier?: { id?: string } }).identifier?.id ?? "")
      : [],
  );
  let theme: string | null = null;
  const settings = decode(profile.settings) as { settings?: unknown } | null;
  if (settings && typeof settings.settings === "string") theme = colorThemeOf(settings.settings);
  return { extensions, theme, name: typeof profile.name === "string" ? profile.name : null };
}

/** "workbench.colorTheme" de um settings.json (aceita comentários e vírgulas sobrando, como o VS Code). */
export function colorThemeOf(settingsJson: string): string | null {
  let theme: string | null = null;
  // A última linha que vale (linhas comentadas com // ficam de fora), como o VS Code lê.
  for (const line of settingsJson.split("\n")) {
    if (line.trim().startsWith("//")) continue;
    const m = /"workbench\.colorTheme"\s*:\s*"((?:[^"\\]|\\.)*)"/.exec(line);
    if (m) theme = m[1]!.replace(/\\(.)/g, "$1");
  }
  return theme;
}

/** Id do gist num link de perfil do VS Code (vscode.dev/profile/github/<id> ou gist.github.com/…/<id>). */
export function profileGistId(url: string): string | null {
  const m =
    /^https:\/\/(?:insiders\.)?vscode\.dev\/profile\/github\/([0-9a-f]{20,40})\/?$/i.exec(url.trim()) ??
    /^https:\/\/gist\.github\.com\/(?:[A-Za-z0-9-]+\/)?([0-9a-f]{20,40})\/?$/i.exec(url.trim());
  return m ? m[1]!.toLowerCase() : null;
}
